const hiddenSimilarAttributeKeys = new Set([
  "Spider_Source",
  "Spider_Source_Product_Code",
  "Spider_Source_Product_Id",
  "Spider_Source_Url",
  "Spider_Source_Image_Url",
  "Spider_Source_Category_Name",
  "Spider_Source_Category_Path",
  "TODO_Remark",
]);

/**
 * 判断属性值是否可用于相似产品筛选。
 * @param {unknown} value 商品属性值
 * @returns {boolean} 是否为有效筛选值
 */
const hasSimilarFilterValue = (value) => {
  const normalizedValue = `${value ?? ""}`.trim();

  return Boolean(
    normalizedValue &&
      normalizedValue !== "-" &&
      normalizedValue !== "_" &&
      normalizedValue.toUpperCase() !== "N/A"
  );
};

/**
 * 判断属性名是否可用于相似产品筛选。
 * @param {string} attributeName 商品属性名称
 * @returns {boolean} 是否为有效筛选属性
 */
const isVisibleSimilarAttribute = (attributeName = "") => {
  return (
    attributeName &&
    !attributeName.startsWith("Spider_") &&
    !hiddenSimilarAttributeKeys.has(attributeName)
  );
};

/**
 * 根据当前商品属性生成相似产品筛选条件。
 * @param {Record<string, any>} product 当前商品
 * @param {string[] | undefined} selectedAttributeNames 已勾选的属性名；未传时保留全部有效属性
 * @returns {Record<string, string>} 可传给商品列表接口的属性筛选条件
 */
export const buildSimilarProductFilters = (
  product = {},
  selectedAttributeNames
) => {
  const attributes = product?.product_Attributes || {};
  const selectedAttributeSet = Array.isArray(selectedAttributeNames)
    ? new Set(selectedAttributeNames)
    : null;

  return Object.keys(attributes).reduce((filters, attributeName) => {
    const value = attributes[attributeName];
    const isSelected =
      selectedAttributeSet === null || selectedAttributeSet.has(attributeName);

    if (
      isSelected &&
      isVisibleSimilarAttribute(attributeName) &&
      hasSimilarFilterValue(value)
    ) {
      filters[attributeName] = `${value}`.trim();
    }

    return filters;
  }, {});
};

/**
 * 根据当前商品生成相似产品列表页地址。
 * @param {Record<string, any>} product 当前商品
 * @param {string[] | undefined} selectedAttributeNames 已勾选的属性名；未传时使用全部有效属性
 * @returns {string} 商品列表页 URL
 */
export const buildSimilarProductsPath = (product = {}, selectedAttributeNames) => {
  const searchParams = new URLSearchParams();
  const filters = buildSimilarProductFilters(product, selectedAttributeNames);

  if (product?.category) {
    searchParams.set("category", product.category);
  }

  if (product?.sup_category) {
    searchParams.set("sup_category", product.sup_category);
  }

  if (product?._id) {
    searchParams.set("sourceProductId", product._id);
  }

  searchParams.set("similar", "1");
  searchParams.set("product_Attributes", JSON.stringify(filters));

  return `/products?${searchParams.toString()}`;
};

/**
 * 从 URL 参数中解析商品属性筛选条件。
 * @param {string | null} value URL 中的 product_Attributes 参数
 * @returns {Record<string, any>} 已解析的属性筛选条件
 */
export const parseProductAttributesParam = (value) => {
  if (!value) return {};

  try {
    const parsedValue = JSON.parse(value);
    return parsedValue && typeof parsedValue === "object" && !Array.isArray(parsedValue)
      ? parsedValue
      : {};
  } catch (error) {
    // TODO: 如需埋点，可在这里记录异常的筛选参数。
    return {};
  }
};
